import { Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';

/**
 * Coordinates the activation of a newly-installed Service Worker version.
 *
 * Background — the white-screen-on-first-open after a PWA update was caused by
 * an immediate `location.reload()` fired on every `VERSION_READY`. The reload
 * could land on a freshly-served `index.html` (pointing to new chunk hashes)
 * while the SW's app-cache still held the old chunks, or vice versa, yielding
 * 404s on bundle loads and a blank page.
 *
 * The new flow:
 *   1. On `VERSION_READY` → call `swUpdate.activateUpdate()`.
 *   2. Then ask the new SW (via postMessage `CHECK_COHERENCE`) whether its
 *      install completed coherently — i.e. index.html + manifest + all chunks
 *      listed in `build-manifest.json` are in cache.
 *   3. If coherent → silent reload with `?_swu=<ts>` cache-bust querystring.
 *   4. If NOT coherent or no reply within 5s → do nothing; the periodic
 *      `checkForUpdate()` will try again later, when the SW may have completed
 *      installation correctly (e.g. flaky network during install).
 *   5. `unrecoverable` event → hard recovery: unregister all SWs, clear all
 *      caches, then reload. This guarantees the next visit starts clean.
 */
@Injectable({
  providedIn: 'root',
})
export class ServiceWorkerUpdateService {
  // Coalesces interval ticks and visibilitychange firings that land within a
  // few seconds of each other (typical when an iOS PWA returns to foreground
  // and the 30s setInterval also happens to elapse).
  private lastCheckMs = 0;

  // Guards against multiple parallel activation flows when VERSION_READY
  // fires twice in quick succession (can happen on iOS visibilitychange).
  private activating = false;

  constructor(private swUpdate: SwUpdate) {
    if (swUpdate.isEnabled) {
      setInterval(() => this.checkForUpdate(), 30000);

      // Re-check when the PWA returns to the foreground. On iOS standalone
      // setInterval is paused while backgrounded, so without this the device
      // can stay stuck on the previous version after a deploy.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkForUpdate();
        }
      });

      // Handle available updates
      swUpdate.versionUpdates.subscribe((event) => {
        switch (event.type) {
          case 'VERSION_DETECTED':
            console.log('[SWU] New version detected:', event.version);
            break;
          case 'VERSION_READY':
            console.log('[SWU] New version ready, activating:', event.latestVersion);
            this.activateUpdate();
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.error('[SWU] Failed to install new version:', event.error);
            break;
        }
      });

      // Handle unrecoverable state
      swUpdate.unrecoverable.subscribe((event) => {
        console.error('[SWU] Service worker in unrecoverable state:', event.reason);
        this.hardRecovery();
      });
    }
  }

  private activateUpdate() {
    if (this.activating) return;
    this.activating = true;

    this.swUpdate
      .activateUpdate()
      .then(() => this.checkCoherenceThenReload())
      .catch((err) => {
        console.error('[SWU] activateUpdate() failed', err);
        this.activating = false;
      });
  }

  /**
   * Asks the controlling Service Worker if its install was coherent (index +
   * manifest + every chunk pre-cached). Only reloads when the answer is yes;
   * otherwise leaves the user on the still-working current version.
   */
  private async checkCoherenceThenReload(): Promise<void> {
    try {
      const sw = navigator.serviceWorker?.controller;
      if (!sw) {
        // No controller (rare — e.g. very first install on a new origin).
        // Just reload; first install can't be incoherent in the white-screen
        // sense since there's no previous version to mismatch against.
        this.reloadWithCacheBust();
        return;
      }

      const coherent = await this.queryCoherence(sw, 5000);
      if (coherent) {
        console.log('[SWU] New SW is coherent — reloading');
        this.reloadWithCacheBust();
      } else {
        console.warn(
          '[SWU] New SW reported incoherent (or timeout) — skipping reload; will retry on next checkForUpdate',
        );
        this.activating = false;
      }
    } catch (err) {
      console.error('[SWU] coherence check failed', err);
      this.activating = false;
    }
  }

  private queryCoherence(sw: ServiceWorker, timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
      let settled = false;
      const channel = new MessageChannel();

      const finalize = (value: boolean) => {
        if (settled) return;
        settled = true;
        try {
          channel.port1.close();
        } catch {
          /* noop */
        }
        resolve(value);
      };

      channel.port1.onmessage = (ev) => {
        const data = ev.data as { coherent?: boolean; version?: string } | null;
        finalize(!!(data && data.coherent));
      };

      try {
        sw.postMessage({ type: 'CHECK_COHERENCE' }, [channel.port2]);
      } catch (err) {
        console.error('[SWU] postMessage CHECK_COHERENCE failed', err);
        finalize(false);
        return;
      }

      setTimeout(() => finalize(false), timeoutMs);
    });
  }

  private reloadWithCacheBust() {
    // ?_swu=<ts> guarantees the navigation request bypasses HTTP cache
    // without relying on the deprecated location.reload(true) signature.
    // Hash is preserved so the user lands back on the same in-app route.
    const url = `${window.location.pathname}?_swu=${Date.now()}${window.location.hash}`;
    window.location.replace(url);
  }

  /**
   * Recovery path used when SwUpdate reports `unrecoverable`. Wipes all SWs +
   * caches and reloads from scratch. This is also what the inline recovery
   * overlay in index.html calls when bootstrap fails or chunk loads 404.
   */
  private async hardRecovery() {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in self) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (err) {
      console.error('[SWU] hardRecovery cleanup failed', err);
    } finally {
      window.location.replace(`${window.location.pathname}?_swrec=${Date.now()}`);
    }
  }

  // Force check for updates (can be called manually). Coalesces calls fired
  // within 5s so the interval tick + visibilitychange handler don't double-fire.
  checkForUpdate() {
    if (!this.swUpdate.isEnabled) return;
    const now = Date.now();
    if (now - this.lastCheckMs < 5000) return;
    this.lastCheckMs = now;
    this.swUpdate.checkForUpdate();
  }

  // Force update (can be called manually)
  forceUpdate() {
    if (this.swUpdate.isEnabled) {
      this.activateUpdate();
    }
  }
}
